// src/components/outreach/PitchCard.tsx
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pitch } from '../../types/outreach';
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { Badge } from "@/components/ui/badge";
import { Users, Star, ListMusic, Mail } from "lucide-react";
import { formatListenerCount } from "@/utils/format-utils";

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useUser } from '../../providers/user-provider';

interface PitchCardProps {
  pitch: Pitch;
  overlay?: boolean;
  onCompose?: (pitch: Pitch) => void;
}

export function PitchCard({ pitch, overlay, onCompose }: PitchCardProps) {
  const navigate = useNavigate();
  const { user } = useUser();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: pitch.id, data: { pitch } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none', // Required for PointerSensor
  };

  const handleCardClick = () => {
    // Prevent navigation if we are dragging
    if (isDragging) return;
    navigate(`/dashboard/podcasts/${pitch.podcastId}`);
  };

  const PodcastContent = () => (
    <div className="flex gap-3 items-start">
        <div className="size-12 rounded-md bg-muted flex-shrink-0 overflow-hidden border border-border/50">
           <ImageWithFallback
                src={pitch.podcast?.thumbnailUrl || pitch.podcast?.imageUrl}
                alt={pitch.podcast?.title || "Podcast"}
                className="size-full object-cover"
            />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
            <h4 className="font-semibold text-sm leading-tight line-clamp-2" title={pitch.podcast?.title}>
                {pitch.podcast?.title || `Podcast ${pitch.podcastId}`}
            </h4>
            <div className="flex items-center gap-1.5 flex-wrap">
                {pitch.podcast?.appleRating && (
                    <div className="flex items-center gap-0.5 text-[9px] bg-amber-50 text-amber-700 px-1 rounded h-4 border border-amber-100/50" title={`Based on ${pitch.podcast.appleRatingCount} ratings`}>
                        <Star className="size-2 fill-amber-500 text-amber-500" />
                        <span className="font-medium">{pitch.podcast.appleRating.toFixed(1)}</span>
                        {pitch.podcast.appleRatingCount && <span className="text-amber-600/70">({pitch.podcast.appleRatingCount})</span>}
                    </div>
                )}
                
                {pitch.podcast?.estimatedListeners != null && pitch.podcast.estimatedListeners > 0 && (
                     <Badge variant="secondary" className="text-[9px] px-1 h-4 gap-0.5 bg-indigo-50 text-indigo-700 border-indigo-100/50 hover:bg-indigo-100">
                        <Users className="size-2.5" />
                        {formatListenerCount(pitch.podcast.estimatedListeners)}
                    </Badge>
                )}

                {pitch.podcast?.episodeCount && (
                     <Badge variant="outline" className="text-[9px] px-1 h-4 gap-0.5 border-slate-200 text-slate-500 font-normal">
                        <ListMusic className="size-2.5" />
                        {pitch.podcast.episodeCount}
                    </Badge>
                )}

                {(pitch.podcast?.hasEmail || pitch.podcast?.email) && (
                    <div className="flex items-center justify-center size-4 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100" title="Email available">
                        <Mail className="size-2.5" />
                    </div>
                )}
            </div>
             {pitch.notes && pitch.notes !== 'Added from Podcast Details' && (
                <div className="text-[10px] text-muted-foreground italic bg-slate-50 p-1 rounded mt-1 line-clamp-1 border border-slate-100">
                   {pitch.notes}
                </div>
             )}
        </div>
        <div className="flex-col gap-1 items-end pl-2">
            {user?.subscriptionTier === 'ENTERPRISE' && (
                <Button
                    size="icon"
                    variant="ghost"
                    className="size-7 rounded-full hover:bg-slate-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onCompose) onCompose(pitch);
                    }}
                    disabled={!pitch.podcastId } // Should be always valid
                    title={pitch.status === 'DRAFT' ? 'Compose Email' : 'View Thread'}
                >
                    <Mail className="size-4 text-slate-500 hover:text-primary transition-colors" />
                </Button>
            )}
        </div>
    </div>
  );
  
  if (overlay) {
      return (
        <Card className="mb-2 bg-white shadow-xl cursor-grabbing border-primary/60 ring-2 ring-primary/10">
            <CardContent className="p-3">
                <PodcastContent />
            </CardContent>
        </Card>
      );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="mb-2 touch-none h-auto">
      <Card 
        className={cn(
          "bg-white border transition-all hover:border-primary/50 cursor-grab active:cursor-grabbing hover:shadow-sm",
          isDragging && "opacity-0" // Hide original when dragging (overlay is shown)
        )}
        onClick={handleCardClick}
      >
        <CardContent className="p-3">
            <PodcastContent />
        </CardContent>
      </Card>
    </div>
  );
}
